import React, { Fragment } from "react";
import { ConnectedRouter } from "connected-react-router";
import { Route, Switch } from "react-router-dom";
import history from "../store/history";
import PrivateRoute from "./PrivateRoute";
import LoginRoute from "./LoginRoute";

import { types, permissions, checkAcl } from "../utils/acl"

import Alerts from "../components/alerts";
import DashboardPage from "../pages/Dashboard";
import HelpPage from "../pages/Help";
import NotFoundPage from "../pages/NotFound";
import Login from "../pages/Login";

import ViewLocation from "../pages/location/ViewLocation";
import AddLocation from "../pages/location/AddLocation";
import SearchLocation from "../pages/location/SearchLocation";
import SearchLocationUser from "../pages/location/SearchLocationUser";
import GooglePlaceIdFinder from "../pages/google/PlaceIdFinder";
import LocCommentsList from "../pages/location/CommentsList";

import AdmAreas from "../pages/adm-areas/AdmAreas";
import ViewAdmArea from "../pages/adm-areas/ViewAdmArea";
import AdmAreaLocations from "../pages/adm-areas/AdmAreaLocations";
import AdmCommentsList from "../pages/adm-areas/CommentsList";

import SearchActivities from "../pages/activities/SearchActivities";

import CategoryList from "../pages/category/CategoryList";

import SearchTravalour from "../pages/travalours/SearchTravalour";
import ViewTravalour from "../pages/travalours/ViewTravalour";
import AccountDeleteRequest from "../pages/travalours/AccountDeleteRequest";

import SearchTravelCard from "../pages/travel-cards/SearchTravelCard";
import ViewTravelCard from "../pages/travel-cards/ViewTravelCard";

import ViewTrip from "../pages/trips/ViewTrip";
import SearchTrip from "../pages/trips/SearchTrip";

import SearchArticle from "../pages/articles/SearchArticle";
import ViewArticle from "../pages/articles/ViewArticle";

import SpamReports from "../pages/spam/SpamReports";

import ChangePassword from "../pages/profile/ChangePassword";

import Roles from "../pages/acl/Roles";
import Users from "../pages/acl/Users";

import SystemDocuments from "../pages/system/Documents";

import ApiDocsList from "../pages/dev-settings/api-doc/ApiDocsList";
import ApiDocDetails from "../pages/dev-settings/api-doc/ApiDocDetails";
import ViewApiDoc from "../pages/dev-settings/api-doc/ViewApiDoc";

import WebsocketClient from "../pages/dev-settings/websocket-client/WebsocketClient";

import SearchFeedback from "../pages/feedback/SearchFeedback";

import TestPage from "../pages/TestPage";

export default function AppRouter() {
  const LOCATION_ACCESS = checkAcl({ p: permissions.LOCATION_MANAGEMENT, t: types.VIEW_ONLY });
  const DASHBOARD_ACCESS = checkAcl({ p: permissions.ADMIN_WEB_DASHBOARD, t: types.VIEW_ONLY });
  const CATEGORY_ACCESS = checkAcl({ p: permissions.CATEGORY_MANAGEMENT, t: types.VIEW_ONLY });
  const ACL_ACCESS = checkAcl({ p: permissions.ADMIN_WEB_USER_MANAGEMENT, t: types.VIEW_ONLY });
  const TRAVALOURS_ACCESS = checkAcl({ p: permissions.TRAVALOUR_USER_MANAGEMENT, t: types.VIEW_ONLY });
  const USER_POSTS_ACCESS = checkAcl({ p: permissions.TRAVALOUR_USER_POSTS, t: types.VIEW_ONLY });
  const ADMIN_AREA_ACCESS = checkAcl({ p: permissions.ADMINISTRATIVE_AREA_MANAGEMENT, t: types.VIEW_ONLY });
  const SPAM_ACCESS = checkAcl({ p: permissions.SPAM_MANAGEMENT, t: types.VIEW_ONLY });
  const SYSTEM_ACCESS = checkAcl({ p: permissions.SYSTEM_SETTINGS_MANAGEMENT, t: types.VIEW_ONLY });
  const DEV_ACCESS = checkAcl({ p: permissions.DEV_SETTINGS, t: types.VIEW_ONLY });
  const AMENITIES_ACCESS = checkAcl({ p: permissions.AMENITIES, t: types.VIEW_ONLY });

  return (
    <Fragment>
      <Alerts />
      <ConnectedRouter history={history}>
        <Switch>
          <LoginRoute path="/" component={Login} exact />
          <PrivateRoute path="/dashboard" component={DashboardPage} hasAccess={DASHBOARD_ACCESS} />

          {/* Location Routes */}
          <PrivateRoute path="/location/search" component={SearchLocation} exact hasAccess={LOCATION_ACCESS} />
          <PrivateRoute path="/location/add" component={AddLocation} exact hasAccess={LOCATION_ACCESS} />
          <PrivateRoute path="/location/search/user" component={SearchLocationUser} exact hasAccess={LOCATION_ACCESS} />
          <PrivateRoute path="/location/google-place-id-finder" component={GooglePlaceIdFinder} exact hasAccess={LOCATION_ACCESS} />
          <PrivateRoute path="/location/:locationId" component={ViewLocation} exact hasAccess={LOCATION_ACCESS} />
          <PrivateRoute path="/location/comment/list" component={LocCommentsList} exact hasAccess={LOCATION_ACCESS} />

          {/* Administrative Area Routes */}
          <PrivateRoute path="/adm-areas" component={AdmAreas} exact hasAccess={ADMIN_AREA_ACCESS} />
          <PrivateRoute path="/adm-areas/:admId/locations" component={AdmAreaLocations} hasAccess={ADMIN_AREA_ACCESS} />
          <PrivateRoute path="/adm-areas/:admId" component={ViewAdmArea} exact hasAccess={ADMIN_AREA_ACCESS} />
          <PrivateRoute path="/adm-areas/comment/list" component={AdmCommentsList} exact hasAccess={ADMIN_AREA_ACCESS} />

          {/* Amenities Routes */}
          <PrivateRoute path="/activities/search" component={SearchActivities} hasAccess={AMENITIES_ACCESS} />

          {/* Category Routes */}
          <PrivateRoute path="/category" component={CategoryList} exact hasAccess={CATEGORY_ACCESS} />

          {/* Travalour User Routes */}
          <PrivateRoute path="/travalour/search" component={SearchTravalour} hasAccess={TRAVALOURS_ACCESS} />
          <PrivateRoute path="/travalour/account-del-req" component={AccountDeleteRequest} hasAccess={TRAVALOURS_ACCESS} />
          <PrivateRoute path="/travalour/:userId" component={ViewTravalour} exact hasAccess={TRAVALOURS_ACCESS} />

          {/* Travelcard Routes */}
          <PrivateRoute path="/travel-card/search" component={SearchTravelCard} hasAccess={USER_POSTS_ACCESS} />
          <PrivateRoute path="/travel-card/:travelCardId" component={ViewTravelCard} exact hasAccess={USER_POSTS_ACCESS} />

          {/* Trip Routes */}
          <PrivateRoute path="/trip/search" component={SearchTrip} hasAccess={USER_POSTS_ACCESS} />
          <PrivateRoute path="/trip/:tripId" component={ViewTrip} exact hasAccess={USER_POSTS_ACCESS} />

          {/* Article Routes */}
          <PrivateRoute path="/article/search" component={SearchArticle} hasAccess={USER_POSTS_ACCESS} />
          <PrivateRoute path="/article/:articleId" component={ViewArticle} exact hasAccess={USER_POSTS_ACCESS} />

          {/* Spam Routes */}
          <PrivateRoute path="/spam" component={SpamReports} exact hasAccess={SPAM_ACCESS} />

          {/* Profile Routes */}
          <PrivateRoute path="/profile/change-pwd" component={ChangePassword} exact hasAccess={true} />

          {/* ACL Routes */}
          <PrivateRoute path="/acl/users" component={Users} exact hasAccess={ACL_ACCESS} />
          <PrivateRoute path="/acl/roles" component={Roles} exact hasAccess={ACL_ACCESS} />

          {/* System Routes */}
          <PrivateRoute path="/sys/documents/:docType" component={SystemDocuments} exact hasAccess={SYSTEM_ACCESS} />

          {/* Dev Settings Routes */}
          <PrivateRoute path="/dev/api-docs" component={ApiDocsList} exact hasAccess={DEV_ACCESS} />
          <PrivateRoute path="/dev/api-docs/detail/:apiDocDetailId" component={ViewApiDoc} exact hasAccess={DEV_ACCESS} />
          <PrivateRoute path="/dev/api-docs/:apiDocId" component={ApiDocDetails} exact hasAccess={DEV_ACCESS} />
          <PrivateRoute path="/dev/ws-client" component={WebsocketClient} exact hasAccess={DEV_ACCESS} />

          {/* Feedback */}
          <PrivateRoute path="/feedback" component={SearchFeedback} exact hasAccess={TRAVALOURS_ACCESS} />

          {/* Miscellaneous Routes */}
          <Route path="/help" component={HelpPage} />
          {/*<PrivateRoute path="/test" component={TestPage} />*/}
          <Route path="/test" component={TestPage} />

          <Route component={NotFoundPage} />
        </Switch>
      </ConnectedRouter>
    </Fragment>
  );
}
